import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';

const ResetPassword = ({ apiEndpoint, onSuccess, onBack }) => {
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    const emailParam = params.get('email');

    if (tokenParam && emailParam) {
      setToken(tokenParam);
      setEmail(decodeURIComponent(emailParam));
    } else {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 8 || newPassword.length > 128) {
      setError('Password must be 8-128 characters long');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${apiEndpoint}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          email,
          newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
      padding: '40px',
      width: '100%',
      maxWidth: '500px'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      color: '#6b7280',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '14px',
      cursor: 'pointer',
      marginBottom: '24px',
      padding: '8px 0',
      transition: 'color 0.3s'
    },
    iconWrapper: {
      display: 'inline-block',
      padding: '12px',
      backgroundColor: '#ede9fe',
      borderRadius: '50%',
      marginBottom: '16px'
    },
    heading: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#111827',
      margin: '0 0 8px 0'
    },
    subheading: {
      color: '#6b7280',
      marginTop: '8px',
      fontSize: '14px'
    },
    successBox: {
      backgroundColor: '#f0fdf4',
      border: '1px solid #bbf7d0',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      textAlign: 'center'
    },
    errorBox: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'flex-start'
    },
    label: {
      display: 'block',
      color: '#374151',
      fontWeight: '600',
      marginBottom: '8px',
      fontSize: '14px'
    },
    inputWrapper: {
      position: 'relative',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 40px 12px 40px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      fontSize: '16px',
      transition: 'border-color 0.3s',
      boxSizing: 'border-box',
      outline: 'none'
    },
    iconLeft: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af'
    },
    iconRight: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0
    },
    primaryButton: {
      width: '100%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      padding: '16px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
    },
    spinner: {
      display: 'inline-block',
      width: '20px',
      height: '20px',
      border: '3px solid rgba(255,255,255,0.3)',
      borderTop: '3px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginRight: '8px'
    }
  };

  if (!token || !email) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center' }}>
            <AlertCircle style={{ color: '#ef4444', marginBottom: '16px' }} size={48} />
            <h2 style={{ ...styles.heading, marginBottom: '8px' }}>Invalid Reset Link</h2>
            <p style={{ ...styles.subheading, marginBottom: '24px' }}>
              This password reset link is invalid or has expired.
            </p>
            <button
              onClick={onBack}
              className="hover-button"
              style={styles.primaryButton}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .hover-button:hover {
          background: linear-gradient(135deg, #5568d3 0%, #63408b 100%);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
        }
        .input-focus:focus {
          border-color: #667eea;
        }
      `}</style>
      
      <div style={styles.card}>
        {!success && (
          <button
            onClick={onBack}
            style={styles.backButton}
            onMouseOver={(e) => e.target.style.color = '#111827'}
            onMouseOut={(e) => e.target.style.color = '#6b7280'}
          >
            <ArrowLeft size={20} style={{ marginRight: '8px' }} />
            Back to Login
          </button>
        )}

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={styles.iconWrapper}>
            <Lock size={32} style={{ color: '#667eea' }} />
          </div>
          <h2 style={styles.heading}>Reset Password</h2>
          <p style={styles.subheading}>
            Enter your new password below
          </p>
        </div>

        {success ? (
          <div>
            <div style={styles.successBox}>
              <CheckCircle style={{ color: '#10b981', margin: '0 auto 16px' }} size={48} />
              <h3 style={{ fontWeight: '600', color: '#065f46', marginBottom: '8px', marginTop: 0, fontSize: '18px' }}>
                Password Reset Successful!
              </h3>
              <p style={{ color: '#047857', margin: 0 }}>
                Your password has been reset successfully. You can now login with your new password.
              </p>
            </div>
            <button
              onClick={onBack}
              className="hover-button"
              style={styles.primaryButton}
            >
              Go to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={styles.errorBox}>
                <AlertCircle style={{ color: '#ef4444', marginTop: '2px', marginRight: '12px', flexShrink: 0 }} size={20} />
                <p style={{ color: '#dc2626', fontSize: '14px', margin: 0 }}>{error}</p>
              </div>
            )}

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                value={email}
                disabled
                style={{
                  ...styles.input,
                  backgroundColor: '#f9fafb',
                  color: '#6b7280',
                  padding: '12px 16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>New Password</label>
              <div style={styles.inputWrapper}>
                <Lock style={styles.iconLeft} size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (8-128 characters)"
                  required
                  minLength={8}
                  maxLength={128}
                  className="input-focus"
                  style={styles.input}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.iconRight}
                  onMouseOver={(e) => e.currentTarget.style.color = '#667eea'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <PasswordStrengthIndicator 
                password={newPassword} 
                showRequirements={newPassword.length > 0}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Confirm New Password</label>
              <div style={styles.inputWrapper}>
                <Lock style={styles.iconLeft} size={20} />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                  className="input-focus"
                  style={styles.input}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.iconRight}
                  onMouseOver={(e) => e.currentTarget.style.color = '#667eea'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px', marginBottom: 0 }}>
                  Passwords do not match
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
              className="hover-button"
              style={{
                ...styles.primaryButton,
                opacity: (isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword) ? 0.5 : 1,
                cursor: (isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword) ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={styles.spinner}></span>
                  Resetting Password...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
